import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CalendarX2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { getBooking, cancelBookingPublic, getPaymentStatus, createCheckout } from "../lib/api";

const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function Manage() {
    const { bookingId } = useParams();
    const [searchParams] = useSearchParams();
    const [booking, setBooking] = useState(null);
    const [error, setError] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [processingFee, setProcessingFee] = useState(false);

    const load = useCallback(
        () =>
            getBooking(bookingId)
                .then(setBooking)
                .catch(() => setError("We couldn't find that booking. Check the link from your email.")),
        [bookingId]
    );

    useEffect(() => {
        const feeSession = searchParams.get("fee_session");
        if (feeSession) {
            setProcessingFee(true);
            getPaymentStatus(feeSession)
                .catch(() => {})
                .finally(() => {
                    load();
                    setProcessingFee(false);
                });
        } else {
            load();
        }
    }, [bookingId, searchParams, load]);

    const sessionDt = booking ? new Date(`${booking.date}T${booking.time}:00`) : null;
    const hoursLeft = sessionDt ? (sessionDt.getTime() - Date.now()) / 36e5 : null;
    const lateFee = hoursLeft !== null && hoursLeft >= 0 && hoursLeft < 24;

    const cancel = async () => {
        setCancelling(true);
        try {
            const r = await cancelBookingPublic(bookingId, window.location.origin);
            if (r.status === "fee_required" && r.checkout_url) {
                window.location.href = r.checkout_url;
                return;
            }
            setBooking({ ...booking, status: "cancelled" });
            toast.success("Booking cancelled — a confirmation email is on its way.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not cancel. Please email us.");
        } finally {
            setCancelling(false);
            setConfirming(false);
        }
    };

    const prepay = async () => {
        try {
            const r = await createCheckout({
                lookup_key: "consulting_prepay",
                quantity: 1,
                origin_url: window.location.origin,
                booking_id: bookingId,
            });
            window.location.href = r.checkout_url;
        } catch (err) {
            toast.error("Could not start payment. Please try again.");
        }
    };

    return (
        <div className="bg-paper text-ink min-h-screen flex items-center justify-center px-5" data-testid="manage-booking-page">
            <div className="w-full max-w-lg border border-ink/10 bg-white p-8 md:p-10">
                <p className="text-xs tracking-[0.25em] uppercase text-ink/50 flex items-center gap-3">
                    <span className="h-px w-8 bg-worx inline-block" /> Consulting WorX
                </p>
                {processingFee && (
                    <p className="mt-4 text-sm text-ink/60 flex items-center gap-2" data-testid="manage-fee-processing">
                        <Loader2 className="animate-spin" size={16} /> Processing your cancellation fee…
                    </p>
                )}
                {error ? (
                    <>
                        <h1 className="mt-4 font-display font-semibold text-3xl tracking-tight">Booking not found</h1>
                        <p className="mt-3 text-ink/60 text-sm" data-testid="manage-error-text">{error}</p>
                    </>
                ) : !booking ? (
                    <div className="mt-8 flex items-center gap-2 text-ink/50" data-testid="manage-loading">
                        <Loader2 className="animate-spin" size={18} /> Loading your booking…
                    </div>
                ) : booking.status === "cancelled" ? (
                    <>
                        <CalendarX2 size={40} className="text-ink/40 mt-4" />
                        <h1 className="mt-4 font-display font-semibold text-3xl tracking-tight">Booking cancelled</h1>
                        <p className="mt-3 text-ink/60 text-sm leading-relaxed" data-testid="manage-cancelled-text">
                            Your {booking.service_name} session on {booking.date} at {fmtTime(booking.time)} is cancelled
                            and the slot is open again.
                        </p>
                        <a
                            href="/#booking"
                            data-testid="manage-rebook-link"
                            className="mt-6 inline-flex items-center gap-2 bg-worx text-white px-6 py-3 text-sm hover:bg-ink transition-colors"
                        >
                            Book a new session <ArrowUpRight size={14} />
                        </a>
                    </>
                ) : (
                    <>
                        <CheckCircle2 size={40} className="text-worx mt-4" />
                        <h1 className="mt-4 font-display font-semibold text-3xl tracking-tight">Your booking</h1>
                        <dl className="mt-6 grid grid-cols-2 gap-y-3 text-sm" data-testid="manage-booking-details">
                            <dt className="text-ink/50">Service</dt><dd>{booking.service_name}</dd>
                            <dt className="text-ink/50">Date</dt><dd>{booking.date}</dd>
                            <dt className="text-ink/50">Time</dt><dd>{fmtTime(booking.time)}</dd>
                            <dt className="text-ink/50">Location</dt>
                            <dd data-testid="manage-location">{booking.location === "in-person" ? "In person" : "Virtual — Google Meet"}</dd>
                            <dt className="text-ink/50">Duration</dt><dd>1 hour</dd>
                            <dt className="text-ink/50">Total</dt><dd>${booking.price.toFixed(2)}</dd>
                            <dt className="text-ink/50">Payment</dt>
                            <dd data-testid="manage-payment-status">{booking.price === 0 ? "Free — discovery call" : booking.payment_status === "paid" ? "Paid" : "Pay by card online, or CashApp/cash after class"}</dd>
                        </dl>
                        <p className="mt-5 text-sm text-ink/60 leading-relaxed">
                            {booking.location === "in-person"
                                ? "Sessions happen on Google Meet, at your place, or the Palm Beach County Main Library (3650 Summit Blvd, West Palm Beach). "
                                : "Your session is virtual — your Google Meet link arrives in a separate Google Calendar invite email. "}
                            To reschedule, cancel here and book a new time — or just reply to your
                            confirmation email.
                        </p>
                        <p className="mt-4 text-xs text-ink/40 leading-relaxed" data-testid="manage-policy-text">
                            Free cancellation up to 24 hours before your session. Inside 24 hours, a $25
                            late-cancellation fee applies; no-shows are charged $25.
                        </p>
                        {!confirming ? (
                            <div className="mt-6 flex flex-wrap gap-3">
                                {booking.payment_status !== "paid" && booking.price > 0 && (
                                    <button
                                        data-testid="manage-prepay-btn"
                                        onClick={prepay}
                                        className="bg-ink text-paper px-6 py-3 text-sm hover:bg-worx transition-colors"
                                    >
                                        Prepay $95 online — save $5
                                    </button>
                                )}
                                <button
                                    data-testid="manage-cancel-btn"
                                    onClick={() => setConfirming(true)}
                                    className="border border-ink/20 px-6 py-3 text-sm hover:border-worx hover:text-worx transition-colors"
                                >
                                    Cancel this booking
                                </button>
                            </div>
                        ) : (
                            <div className="mt-6 border border-worx/30 bg-worx/5 p-4" data-testid="manage-cancel-confirm">
                                {lateFee ? (
                                    <p className="text-sm" data-testid="manage-fee-warning">
                                        This session is within 24 hours — a <strong>$25 late-cancellation fee</strong> applies.
                                        You'll be sent to secure card payment to complete the cancellation.
                                    </p>
                                ) : (
                                    <p className="text-sm">Are you sure? This frees up your slot immediately — no charge.</p>
                                )}
                                <div className="mt-3 flex gap-3">
                                    <button
                                        data-testid="manage-cancel-confirm-btn"
                                        onClick={cancel}
                                        disabled={cancelling}
                                        className="bg-worx text-white px-5 py-2.5 text-sm hover:bg-ink transition-colors"
                                    >
                                        {cancelling ? <Loader2 className="animate-spin" size={14} /> : lateFee ? "Pay $25 & cancel" : "Yes, cancel it"}
                                    </button>
                                    <button
                                        data-testid="manage-cancel-keep-btn"
                                        onClick={() => setConfirming(false)}
                                        className="border border-ink/20 px-5 py-2.5 text-sm hover:border-ink transition-colors"
                                    >
                                        Keep my booking
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
                <Link to="/" data-testid="manage-home-link" className="mt-8 inline-block text-xs text-ink/40 hover:text-worx transition-colors">
                    ← Back to Consulting WorX
                </Link>
            </div>
        </div>
    );
}
